/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What happens to a literal that came out of somebody's real request** — one decision and one
 * set of words, for every surface that imports a sample (`#1187`, `#1188`).
 *
 * A paste is the operator handing us a working example. Most of it is theirs to keep; some of it
 * is a value we build ourselves and would overwrite; and a little of it must never be stored at
 * all. The VAST tag parser has answered this correctly since `#952` and said so per field —
 *
 * > `"ip" carried a literal value from the sample. It is per-request data, so it was mapped to
 * > {ip} and the sample value discarded.`
 *
 * — and the ORTB body paste could not, because both the policy and the sentences describing it
 * lived in `libs/adapters`, which the console cannot import. It discarded a whole section instead
 * and told the operator only that it had *"skipped driver-managed field(s): id, imp"*.
 *
 * So the policy is on the concept (`FieldCatalogEntry.perRequest` / `.consent`, promoted by
 * `#1188`) and the words are here. **The words are the point**: two surfaces explaining the same
 * decision in their own phrasing is how one of them ends up describing behaviour the other does
 * not have, and a paste is the moment an operator is most likely to believe what they are told.
 */

import type { FieldCatalogEntry } from './field-catalog';

/** What may be done with the literal a sample carried at a field. */
export type SampleLiteralOutcome =
  /** Keep it — as the value, or as the default standing behind a mapping. */
  | 'retain'
  /**
   * Discard it: the value belongs to the request rather than to the configuration, so a stored
   * copy would be sent for every viewer.
   */
  | 'discard-per-request'
  /**
   * Discard it, and say so more strongly: a stored consent string asserts a regulatory position
   * on behalf of somebody who never took it.
   */
  | 'discard-consent';

/**
 * What may be kept of the literal at a field carrying this concept.
 *
 * `undefined` — a name the catalog does not know — is **retain**, deliberately. That is the
 * operator's own value: a vendor extension, an account id, the part of a pasted request they
 * could not have known to type, and freezing it is the whole point of importing a sample.
 */
export function sampleLiteralPolicy(concept: FieldCatalogEntry | undefined): SampleLiteralOutcome {
  if (concept?.consent) return 'discard-consent';
  if (concept?.perRequest) return 'discard-per-request';
  return 'retain';
}

/**
 * Why a literal was thrown away, in the operator's terms — `null` when it was kept.
 *
 * @param field the name as it appeared in their sample, so they can find it
 * @param literal the value we are refusing to store, quoted back so the sentence is checkable
 * @param macro the canonical macro it was mapped to instead
 */
export function describeDiscardedLiteral(
  outcome: SampleLiteralOutcome,
  field: string,
  literal: string,
  macro: string,
): string | null {
  switch (outcome) {
    case 'discard-consent':
      return (
        `"${field}" carried the literal consent value "${literal}". A stored consent string asserts a ` +
        `privacy position for users who never took it, so it was mapped to {${macro}} and the ` +
        `sample value discarded.`
      );
    case 'discard-per-request':
      return (
        `"${field}" carried a literal value from the sample. It is per-request data, so it was mapped ` +
        `to {${macro}} and the sample value discarded.`
      );
    default:
      return null;
  }
}

/**
 * Why a pasted field was not imported at all — the class the VAST path has no equivalent of,
 * because a query parameter is never built by us procedurally.
 *
 * **This is the honest form of a drop**, and it only became honest once there was somewhere for
 * the field to still be seen: before the *Sent automatically* panel (`#1117`), discarding a
 * pasted `imp.video.maxduration` removed it from the operator's view entirely, and the note named
 * a *section* rather than the fields it had taken.
 */
export function describeDriverBuiltField(field: string, from: string): string {
  return `"${field}" is built on every request from ${lowerFirst(from)}, so the sample value was not imported. It is listed under Sent automatically, where you can override it.`;
}

/**
 * **A number in a partner's sample read back as a concept member** (reported 2026-09-15).
 *
 * The translation is invisible on the row — the row shows a member word, and the partner sent an
 * integer — so the paste says what it did, naming the list the number was read from.
 */
export function describePastedConceptMember(
  field: string,
  value: string | number,
  member: string,
  concept: string,
  versions: readonly string[],
): string {
  return (
    `"${field}" carried ${value}, which is ${member} in OpenRTB ${versions.join('/')}'s ${concept} list, ` +
    `so it was imported as that ${concept}. It is sent under whichever field name this source's version uses.`
  );
}

/**
 * **A number in a concept field that no member means** — kept, because inventing a member would send
 * something the partner did not say, and said, because a raw number here goes out under the name it
 * was pasted with whatever version the source declares.
 */
export function describeUnmatchedConceptValue(
  field: string,
  wireName: string,
  value: string | number,
  concept: string,
  versions: readonly string[],
): string {
  return (
    `"${field}" carried ${value}, which matches no ${concept} in OpenRTB ${versions.join('/')}'s list that ` +
    `bidBrain can express, so it was kept as written. It is sent as ${wireName}: ${value} whatever version ` +
    `this source speaks.`
  );
}

/** Two spellings of one concept in one sample disagreed, and one of them had to win. */
export function describeConflictingConceptValues(field: string, kept: string): string {
  return `The sample gave "${field}" two different values under two field names; ${kept} was kept.`;
}

/**
 * Value shapes that identify a person. Kept deliberately narrow — a false positive on a static value
 * is a warning about correct behaviour, which trains the operator to ignore the warnings that matter.
 */
const IPV4_RE = /^(?:\d{1,3}\.){3}\d{1,3}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UA_RE = /^(Mozilla|Dalvik|Roku|AppleCoreMedia|okhttp|Opera|AFT)\b|^[A-Za-z][\w.-]*\/[\w.]+ \(/;

/**
 * **Does this literal look like it identifies a person?** — one matcher, for the tag parser and every
 * console paste path (`#1016`).
 *
 * Used two ways, deliberately: as a **warning** under a name the catalog does not know (#952 · item 3
 * — naming is per-SSP, so the catalog will never be complete, and silently converting an unknown name
 * would be the same overreach in the other direction), and as the **drop** predicate on a
 * captured-sample intake.
 *
 * **It warns, it never converts.** The console's `suggestMapping` kept a private copy of these three
 * shapes that *did* convert — an IPv4 under `addr` became `{ip}`, a UUID under a publisher's pod id
 * became the viewer's device id — while the parser beside it said the opposite in its own docblock.
 * Two matchers for one question, disagreeing about what the answer is for; moved here on `#1016`.
 */
export function looksIdentifying(value: string): { macro: string; why: string } | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  if (IPV4_RE.test(v)) return { macro: 'ip', why: 'looks like an IPv4 address' };
  if (UA_RE.test(v)) return { macro: 'ua', why: 'looks like a device user-agent' };
  if (UUID_RE.test(v)) return { macro: 'ifa', why: 'looks like an advertising id (UUID)' };
  return null;
}

/**
 * Why a value that looks like a viewer's was left as written — the warning half of
 * {@link looksIdentifying}, in the words the tag parser has used since `#952`.
 */
export function describeIdentifyingShape(field: string, why: string, macro: string): string {
  return (
    `"${field}" is not a known context parameter, but its value ${why}. ` +
    `If it is per-viewer data, map it to {${macro}} — left as a static value it would ` +
    `be sent unchanged for every viewer.`
  );
}

/**
 * `The pod's ceiling` → `the pod's ceiling`, so a declaration reads inside a sentence.
 *
 * **An acronym keeps its case.** `USD`, `HTTPS creatives required` and `VPAID 1/2 and OMID 1` rendered
 * as *"from uSD"* and *"from hTTPS creatives required"* on every paste of a real bid request — read by
 * the operator at the moment they are most likely to believe it. A word whose second letter is also a
 * capital is a name, not a sentence start.
 */
function lowerFirst(text: string): string {
  if (/^[A-Z]{2}/.test(text)) return text;
  return text.charAt(0).toLowerCase() + text.slice(1);
}
